import React, { useMemo, useState } from 'react';
import { Col, Form, Input, Row, Select } from 'antd';
import Button from '../../components/Button/Button';
import { useForm } from 'antd/lib/form/Form';
import { useTranslation } from 'react-i18next';
import { checkIsFormValid } from '../../utils/formHelpers';
import { Industry, JobTitle, MonthlyDeliveriesNumber } from './enums';

export type FullMerchantDetailsFormValues = {
  businessName: string;
  monthlyDeliveriesNumber: MonthlyDeliveriesNumber;
  industry: string;
  jobTitle: string;
};

export type LightMerchantDetailsFormValues = Pick<FullMerchantDetailsFormValues, 'businessName'>;

type MerchantDetailsFormProps = {
  isExistingMerchant: boolean;
  onFinish: (values: FullMerchantDetailsFormValues | LightMerchantDetailsFormValues) => void;
};

const fieldsFullList = ['businessName', 'monthlyDeliveriesNumber', 'industry', 'jobTitle'];

const rowTopMargin = 14; // 24px - 10px form item margin

const MerchantDetailsForm = ({ onFinish, isExistingMerchant }: MerchantDetailsFormProps) => {
  const {
    t,
    i18n: { language }
  } = useTranslation();

  const [form] = useForm<FullMerchantDetailsFormValues | LightMerchantDetailsFormValues>();
  const [isFormValid, setIsFormValid] = useState(false);

  const monthlyDeliveriesOptions = useMemo(() => {
    return Object.values(MonthlyDeliveriesNumber).map((value, i) => {
      return (
        <Select.Option
          value={value}
          key={`deliveryNumber_${value}`}
          // className={optionStyle}
          data-testid={`deliveryNumberOption_${i}`}
        >
          {value}
        </Select.Option>
      );
    });
  }, [t, language]);

  const industryOptions = useMemo(() => {
    return Object.values(Industry).map((value, i) => {
      return (
        <Select.Option value={value} key={`industry_${value}`} data-testid={`industryOption_${i}`}>
          {t(`onboarding.segmentation.industryOptions.${value}`)}
        </Select.Option>
      );
    });
  }, [t, language]);

  const jobTitleOptions = useMemo(() => {
    return Object.values(JobTitle).map((value, i) => {
      return (
        <Select.Option value={value} key={`jobTitle_${value}`} data-testid={`jobTitleOption_${i}`}>
          {t(`onboarding.segmentation.jobTitleOptions.${value}`)}
        </Select.Option>
      );
    });
  }, [t, language]);

  return (
    <Form
      onFinish={onFinish}
      onValuesChange={() =>
        setIsFormValid(checkIsFormValid(form, isExistingMerchant ? ['businessName'] : fieldsFullList, true))
      }
      form={form}
      layout="vertical"
      data-testid="MerchantDetailsForm"
    >
      <Row>
        <Col xs={24}>
          <Form.Item
            name="businessName"
            label={t('onboarding.segmentation.businessName')}
            rules={[{ required: true, message: '' }]}
          >
            <Input size={'large'} max={255} data-testid="businessName" />
          </Form.Item>
        </Col>
      </Row>
      {!isExistingMerchant && (
        <>
          <Row style={{ marginTop: rowTopMargin }}>
            <Col xs={24}>
              <Form.Item
                name="monthlyDeliveriesNumber"
                label={t('onboarding.segmentation.howManyDeliveries')}
                rules={[{ required: true, message: '' }]}
              >
                <Select size={'large'} data-testid="monthlyDeliveriesNumberSelect">
                  {monthlyDeliveriesOptions}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row style={{ marginTop: rowTopMargin }}>
            <Col xs={24}>
              <Form.Item
                name="industry"
                label={t('onboarding.segmentation.whatIndustry')}
                rules={[{ required: true, message: '' }]}
              >
                <Select size={'large'} data-testid="industrySelect">
                  {industryOptions}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row style={{ marginTop: rowTopMargin }}>
            <Col xs={24}>
              <Form.Item
                name="jobTitle"
                label={t('onboarding.segmentation.whatJobTitle')}
                rules={[{ required: true, message: '' }]}
              >
                <Select size={'large'} data-testid="jobTitleSelect">
                  {jobTitleOptions}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </>
      )}

      <Row style={{ marginTop: 48 }}>
        <Col xs={24}>
          <Button
            htmlType="submit"
            type="primary"
            block
            size="large"
            disabled={!isFormValid}
            data-testid="submitButton"
          >
            {t('onboarding.segmentation.continue')}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default MerchantDetailsForm;
